import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavbarDashbord } from './navbarDashbord';
import { Plus, Edit2, Trash2, Shield, UserX, UserCheck, X, User } from 'lucide-react';
import { getDataComptes, addCompte, updateCompte, deleteCompte } from './action';
import '../styles/admin.css';
import API_BASE_URL from '../config';

export function PageComptes() {
    const dispatch = useDispatch();
    const comptes = useSelector(state => state.comptes) || [];
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const isMainAdmin = currentUser.email === 'lensjamas@gmail.com';
    const canManage = isMainAdmin || currentUser.role?.toLowerCase() === 'admin';

    useEffect(() => {
        if (comptes.length === 0) {
            dispatch(getDataComptes());
        }
    }, [dispatch, comptes.length]);

    // Filtering States
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('Tous les statuts');

    // Confirmation delete modal
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleteError, setDeleteError] = useState(''); // compte object

    // UI States
    // No more add modal state

    // Helper to parse date strings (handles both YYYY-MM-DD and DD/MM/YYYY)
    const parseDateStr = (dateStr) => {
        if (!dateStr) return new Date(0);

        // If it contains '/', assume DD/MM/YYYY
        if (dateStr.includes('/')) {
            const [day, month, year] = dateStr.split('/');
            return new Date(`${year}-${month}-${day}`);
        }

        // Otherwise assume YYYY-MM-DD or standard JS format
        return new Date(dateStr);
    };

    // Filtering & Sorting Logic
    const filteredComptes = comptes.filter(c => {
        const cDate = parseDateStr(c.date);
        cDate.setHours(0, 0, 0, 0);

        const start = startDate ? new Date(startDate) : null;
        if (start) start.setHours(0, 0, 0, 0);

        const end = endDate ? new Date(endDate) : null;
        if (end) end.setHours(23, 59, 59, 999);

        const matchesStatus = statusFilter === 'Tous les statuts' || c.status === statusFilter || c.role === statusFilter;
        const matchesStart = !start || cDate >= start;
        const matchesEnd = !end || cDate <= end;

        return matchesStatus && matchesStart && matchesEnd;
    }).sort((a, b) => (parseInt(b.id) || 0) - (parseInt(a.id) || 0));

    const toggleRole = (compte) => {
        // Super admin is immutable — only lensjamas@gmail.com can be super admin
        if (compte.email === 'lensjamas@gmail.com' || compte.role?.toLowerCase() === 'super admin') return;
        const newRole = compte.role?.toLowerCase() === 'admin' ? 'client' : 'admin';
        dispatch(updateCompte({ ...compte, role: newRole }));
    };

    const changeStatus = (compte) => {
        const cycle = ['Actif', 'Désactivé', 'Bloqué'];
        const currentIndex = cycle.indexOf(compte.status);
        const nextIndex = (currentIndex + 1) % cycle.length;
        dispatch(updateCompte({ ...compte, status: cycle[nextIndex] }));
    };

    const reactivateAccount = (compte) => {
        dispatch(updateCompte({ ...compte, status: 'Actif' }));
    };

    const handleSupprimer = (id) => {
        const compte = comptes.find(c => c.id === id);
        setConfirmDelete(compte || { id, username: "cet utilisateur", email: "email inconnu" });
        setDeleteError('');
    };

    const confirmDeleteAccount = () => {
        if (confirmDelete) {
            setDeleteError('');
            dispatch(deleteCompte(confirmDelete.id))
                .unwrap()
                .then(() => {
                    setConfirmDelete(null);
                })
                .catch((err) => {
                    console.error("Delete account error:", err);
                    const msg = err?.response?.data?.message || err?.message || "Erreur lors de la suppression.";
                    setDeleteError(`Erreur: ${msg}`);
                });
        }
    };

    return (
        <div className="admin-layout">
            <NavbarDashbord />
            <main className="admin-main-content">
                <header className="admin-header">
                    <div className="admin-title-group">
                        <h1>Gestion des Comptes</h1>
                        <p>Contrôlez les accès utilisateurs et les permissions ({filteredComptes.length} trouvés).</p>
                    </div>
                </header>

                <div className="admin-table-container">
                    <div className="table-header-actions" style={{ flexWrap: 'wrap', gap: '15px' }}>
                        <div className="admin-filters" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div className="date-range-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div className="date-field-item" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ fontSize: '0.7rem', color: 'var(--admin-text-muted)' }}>De:</span>
                                    <input type="date" className="admin-select date-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
                                </div>
                                <div className="date-field-item" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ fontSize: '0.7rem', color: 'var(--admin-text-muted)' }}>À:</span>
                                    <input type="date" className="admin-select date-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
                                </div>
                                {(startDate || endDate) && <button onClick={() => { setStartDate(''); setEndDate(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-danger)' }}><X size={14} /></button>}
                            </div>
                            <select className="admin-select status-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                <option>Tous les statuts</option>
                                <option>Actif</option>
                                <option>Désactivé</option>
                                <option>Bloqué</option>
                            </select>
                        </div>
                    </div>

                    <table className="account-table">
                        <thead>
                            <tr>
                                <th style={{ minWidth: '160px' }}>Nom complet</th>
                                <th>Role</th>
                                <th>Email</th>
                                <th style={{ minWidth: '100px' }}>Mot de passe</th>
                                <th>Inscription</th>
                                <th>Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredComptes.map((c) => (
                                <tr key={c.id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(15, 23, 42, 0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                                {c.image_user ? (
                                                    <img src={c.image_user?.startsWith('data:image') ? c.image_user : `${API_BASE_URL.replace('/api', '')}/storage/photos/${c.image_user}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <User size={20} color="var(--admin-primary)" />
                                                )}
                                            </div>
                                            <span style={{ fontWeight: '800', color: 'var(--admin-primary)' }}>{c.username}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span style={{
                                            fontSize: '0.7rem',
                                            fontWeight: '900',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            color: c.role?.toLowerCase() === 'super admin'
                                                ? '#700000'
                                                : c.role?.toLowerCase() === 'admin'
                                                    ? 'var(--admin-secondary)'
                                                    : 'var(--admin-text-muted)'
                                        }} className='role'>
                                            {c.role}
                                        </span>
                                    </td>
                                    <td>{c.email}</td>
                                    <td style={{ fontSize: '0.8rem', color: 'var(--admin-text-main)', fontFamily: 'monospace', padding: '4px 8px', borderRadius: '4px' }}>
                                        {c.password ? c.password : <span style={{ color: '#ccc', fontStyle: 'italic' }}>Non défini</span>}
                                    </td>
                                    <td style={{ fontSize: '0.85rem' }}>{c.date}</td>
                                    <td>
                                        <span
                                            className={`account-badge ${c.status === 'Actif' ? 'account-badge-success' : c.status === 'Désactivé' ? 'account-badge-warning' : 'account-badge-danger'}`}
                                            onClick={() => canManage && c.email !== 'lensjamas@gmail.com' && (isMainAdmin || c.email !== currentUser.email) && changeStatus(c)}
                                            style={{ cursor: (canManage && c.email !== 'lensjamas@gmail.com' && (isMainAdmin || c.email !== currentUser.email)) ? 'pointer' : 'not-allowed', opacity: (!isMainAdmin && c.email === currentUser.email) ? 0.6 : 1 }}
                                            title={c.email === 'lensjamas@gmail.com' ? "Statut du Super Admin protégé" : (!isMainAdmin && c.email === currentUser.email ? "Action bloquée" : (canManage ? "Cliquez pour changer le cycle de statut" : "Action réservée aux Administrateurs"))}
                                        >
                                            {c.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="account-action-btns">
                                            {canManage && c.email !== 'lensjamas@gmail.com' && (
                                                <>
                                                    {isMainAdmin && (
                                                        <button
                                                            className={`account-btn-icon ${c.role?.toLowerCase() === 'admin' ? 'account-btn-danger' : 'account-btn-success'}`}
                                                            title={c.role?.toLowerCase() === 'admin' ? "Rendre Client" : "Rendre Admin"}
                                                            onClick={() => toggleRole(c)}
                                                            style={{ color: c.role?.toLowerCase() === 'admin' ? 'var(--admin-danger)' : 'var(--admin-success)' }}
                                                        >
                                                            {c.role?.toLowerCase() === 'admin' ? <User size={16} /> : <Shield size={16} />}
                                                        </button>
                                                    )}
                                                    <button 
                                                        className="account-btn-icon account-btn-modify" 
                                                        onClick={() => (isMainAdmin || c.email !== currentUser.email) && changeStatus(c)} 
                                                        title={!isMainAdmin && c.email === currentUser.email ? "Action bloquée" : "Modifier Statut"}
                                                        style={!isMainAdmin && c.email === currentUser.email ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button 
                                                        className="account-btn-icon account-btn-delete" 
                                                        onClick={() => (isMainAdmin || c.email !== currentUser.email) && handleSupprimer(c.id)} 
                                                        title={!isMainAdmin && c.email === currentUser.email ? "Action bloquée" : "Supprimer Account"}
                                                        style={!isMainAdmin && c.email === currentUser.email ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredComptes.length === 0 && (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--admin-text-muted)' }}>Aucun compte correspondant.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

            </main>

            {/* Confirmation Delete Modal */}
            {confirmDelete && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <style>{`
                        @keyframes shakeError {
                            0%, 100% { transform: translateX(0); }
                            20%, 60% { transform: translateX(-5px); }
                            40%, 80% { transform: translateX(5px); }
                        }
                    `}</style>
                    <div style={{ background: 'var(--admin-card-bg)', borderRadius: '16px', padding: '36px', maxWidth: '420px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', border: '1px solid var(--admin-border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' }}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Trash2 size={22} color="var(--admin-danger)" />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--admin-text-main)', fontWeight: 700 }}>Supprimer le compte</h3>
                                <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--admin-text-muted)' }}>Cette action est irréversible.</p>
                            </div>
                        </div>

                        {deleteError && (
                            <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '0.85rem', fontWeight: 600, animation: 'shakeError 0.4s ease-in-out', border: '1px solid #fca5a5' }}>
                                {deleteError}
                            </div>
                        )}

                        <p style={{ fontSize: '0.9rem', color: 'var(--admin-text-main)', marginBottom: '24px', lineHeight: 1.6 }}>
                            Êtes-vous sûr de vouloir supprimer le compte de <strong>{confirmDelete.username}</strong> ({confirmDelete.email}) ? Toutes ses données seront perdues.
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button onClick={() => { setConfirmDelete(null); setDeleteError(''); }} style={{ padding: '10px 22px', borderRadius: '8px', border: '1px solid var(--admin-border)', background: 'transparent', color: 'var(--admin-text-main)', cursor: 'pointer', fontWeight: 600 }}>Annuler</button>
                            <button onClick={confirmDeleteAccount} style={{ padding: '10px 22px', borderRadius: '8px', border: 'none', background: 'var(--admin-danger)', color: '#fff', cursor: 'pointer', fontWeight: 700 }}>Supprimer définitivement</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
